import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import brandLogo from "@/assets/brand-logo.png";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      document.body.style.backgroundColor = "black";
      document.documentElement.style.backgroundColor = "black";
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [menuOpen]);

  return (
    <>
      <nav
        data-testid="navbar"
        className="fixed top-0 left-0 right-0 z-50 p-4 pointer-events-none flex flex-col items-center"
      >
        {/* Floating pill wrapper: size matches contents dynamically, exactly like Scrapr */}
        <div 
          className={`pointer-events-auto backdrop-blur-xl rounded-full border flex items-center py-1.5 pr-3 pl-4 md:py-2 md:pr-2 md:pl-5 w-auto whitespace-nowrap transition-all duration-500 ease-out ${
            menuOpen 
              ? "bg-black/90 border-white/40 shadow-[0_0_40px_rgba(255,255,255,0.12)]" 
              : "bg-black/95 border-white/10 shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
          }`}
        >
          {/* Logo and Name */}
          <a href="#" className="flex items-center gap-2" data-testid="navbar-logo">
            <img
              src={brandLogo}
              alt="EDITH"
              className="h-[36px] md:h-[34px] w-auto object-contain brightness-0 invert translate-y-[1px] md:translate-y-[2px]"
            />
            <span className="text-white font-bold text-sm md:text-base tracking-[0.1em] uppercase font-['Space_Mono']">EDITH</span>
          </a>

          {/* Divider - visible on both mobile and desktop */}
          <div className="w-[1px] h-4 bg-white/25 ml-3 mr-1 md:mx-3" />

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-[2px]">
            <a href="#ai-preview" className="text-[13px] font-medium text-white bg-white/10 hover:bg-white/[0.18] transition-all py-1 px-3 rounded-full no-underline flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />AI Preview</a>
            <a href="#solution" className="text-[13px] font-normal text-white/55 hover:text-white transition-colors py-1 px-3 rounded-full no-underline">Protocol</a>
            <a href="#problem" className="text-[13px] font-normal text-white/55 hover:text-white transition-colors py-1 px-3 rounded-full no-underline">Problem</a>
            <a href="#social-proof" className="text-[13px] font-normal text-white/55 hover:text-white transition-colors py-1 px-3 rounded-full no-underline">Community</a>
            <a href="#faq" className="text-[13px] font-normal text-white/55 hover:text-white transition-colors py-1 px-3 rounded-full no-underline">FAQ</a>
            <a href="#footer" className="text-[13px] font-normal text-white/55 hover:text-white transition-colors py-1 px-3 rounded-full no-underline">Contact</a>
          </nav>

          {/* Desktop CTA Button */}
          <a
            href="#final-cta"
            className="hidden md:inline-flex items-center text-[13px] font-medium text-[var(--nm-text-light)] bg-[var(--nm-light)] border border-white/10 py-1.5 px-4 rounded-full hover:opacity-80 transition-all ml-2 no-underline"
          >
            Join the Waitlist
          </a>

          {/* Mobile menu button only (aesthetic micro-animated hamburger) */}
          <div className="flex md:hidden items-center ml-2">
            <button
              className="flex flex-col justify-center items-center gap-[5px] w-6 h-6 focus:outline-none"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle Menu"
            >
              <span className={`h-[2px] bg-white rounded-full transition-all duration-300 origin-center ${menuOpen ? 'w-6 translate-y-[7px] rotate-45' : 'w-5'}`} />
              <span className={`h-[2px] bg-white rounded-full transition-all duration-300 ${menuOpen ? 'w-0 opacity-0' : 'w-3'}`} />
              <span className={`h-[2px] bg-white rounded-full transition-all duration-300 origin-center ${menuOpen ? 'w-6 -translate-y-[7px] -rotate-45' : 'w-4'}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Full Screen Menu */}
      {menuOpen && (
        <div className="fixed top-0 left-0 w-full h-[100dvh] bg-black z-40 flex flex-col items-center justify-center md:hidden touch-none overscroll-none">
          <div className="flex flex-col items-center space-y-5 mt-10">
            <a href="#ai-preview" onClick={() => setMenuOpen(false)} className="text-white text-[17px] font-medium bg-white/10 hover:bg-white/20 px-4 py-1.5 rounded-full transition-all no-underline flex items-center gap-2"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />AI Preview</a>
            <a href="#solution" onClick={() => setMenuOpen(false)} className="text-white/60 text-[17px] font-medium hover:text-white transition-colors no-underline">Protocol</a>
            <a href="#problem" onClick={() => setMenuOpen(false)} className="text-white/60 text-[17px] font-medium hover:text-white transition-colors no-underline">Problem</a>
            <a href="#social-proof" onClick={() => setMenuOpen(false)} className="text-white/60 text-[17px] font-medium hover:text-white transition-colors no-underline">Community</a>
            <a href="#faq" onClick={() => setMenuOpen(false)} className="text-white/60 text-[17px] font-medium hover:text-white transition-colors no-underline">FAQ</a>
            <a href="#footer" onClick={() => setMenuOpen(false)} className="text-white/60 text-[17px] font-medium hover:text-white transition-colors no-underline">Contact</a>
            
            <a href="#final-cta" onClick={() => setMenuOpen(false)} className="!mt-8 px-6 py-2.5 rounded-full text-[var(--nm-text-light)] bg-[var(--nm-light)] border border-white/10 text-[15px] font-medium hover:opacity-80 transition-all no-underline">
              Join the Waitlist
            </a>
          </div>
        </div>
      )}
    </>
  );
}
